import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type ReactNode, useState } from "react";

import { BankLogo } from "@dfp/react-components";

import type { BankFlowProps, BankFlowStep } from "./bankFlow.types.ts";

// Replica of MONETA Money Bank's PSD2 pages (see Confluence "Screen flow when
// connecting to a bank" → MONETA). The copy is intentionally hard-coded Czech —
// these are external bank pages, not localized app UI. MONETA's shots have no
// separate SCA push step, so the flow goes login → accounts directly.
const MONETA_PURPLE = "#7A2E8E";
const MONETA_NAVY = "#1A2233";
const MONETA_PAGE_BG = "#f4f5f7";
const MONETA_LAVENDER = "#ECEAF5";
const MONETA_BORDER = "#d9d9dd";

const FONT_STACK = '"Helvetica Neue", Helvetica, Arial, sans-serif';

function MonetaHeader({ onCancel }: { onCancel: () => void }) {
  return (
    <Box sx={{ bgcolor: "#fff", borderBottom: `1px solid ${MONETA_BORDER}` }}>
      <Container maxWidth="lg">
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            gap: 2,
          }}
        >
          <Stack direction="row" sx={{ alignItems: "center", gap: 1.5 }}>
            <BankLogo
              bank="MONETA"
              style={{ height: 22, width: "auto", display: "block" }}
            />
          </Stack>

          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{
              color: MONETA_NAVY,
              borderColor: MONETA_BORDER,
              borderRadius: 5,
              textTransform: "none",
              fontFamily: FONT_STACK,
              fontSize: 14,
              px: 2.5,
              "&:hover": { bgcolor: "#f5f5f5", borderColor: MONETA_BORDER },
            }}
          >
            ‹ Zrušit přihlášení a vrátit se zpět
          </Button>

          <Typography
            sx={{ color: MONETA_NAVY, fontSize: 14, fontFamily: FONT_STACK }}
          >
            📞 224 443 636
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

function MonetaConsentPanel({ thirdPartyName }: { thirdPartyName: string }) {
  return (
    <Box sx={{ bgcolor: MONETA_LAVENDER, borderRadius: 2, p: 3.5 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 20,
          color: MONETA_NAVY,
          fontFamily: FONT_STACK,
          mb: 2,
        }}
      >
        Společnost {thirdPartyName} žádá o přístup k vašemu účtu
      </Typography>

      <Typography
        sx={{
          fontSize: 14,
          color: MONETA_NAVY,
          fontFamily: FONT_STACK,
          mb: 1.5,
        }}
      >
        Přihlášením dáváte společnosti &quot;{thirdPartyName}&quot; právo:
      </Typography>

      <Typography
        sx={{
          fontSize: 14,
          color: MONETA_NAVY,
          fontFamily: FONT_STACK,
          mb: 1.5,
        }}
      >
        - na přístup k informacím o Vašich platebních účtech včetně přehledu
        transakcí
      </Typography>

      <Typography
        sx={{ fontSize: 14, color: MONETA_NAVY, fontFamily: FONT_STACK }}
      >
        - zadat platební příkaz z Vašich platebních účtů
      </Typography>
    </Box>
  );
}

function MonetaFieldLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 700,
        color: MONETA_NAVY,
        fontFamily: FONT_STACK,
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

const monetaInputSx = {
  bgcolor: "#fff",
  width: "100%",
  "& .MuiOutlinedInput-root": { borderRadius: 1 },
  "& input": { py: 1, px: 1.25, fontSize: 14, fontFamily: FONT_STACK },
};

// Day / month dropdown values for the "Den a měsíc narození" SCA shortcut.
const DAYS = Array.from({ length: 31 }, (_, index) => String(index + 1));
const MONTHS = Array.from({ length: 12 }, (_, index) => String(index + 1));

/** MONETA Money Bank PSD2 flow: QR / ID login → account consent. */
export function MonetaFlow({
  thirdPartyName,
  ownerName,
  username,
  accounts,
  initialStep,
  onConnect,
  onCancel,
}: BankFlowProps) {
  const [step, setStep] = useState<BankFlowStep>(initialStep);
  const [loginName, setLoginName] = useState(username);
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(accounts.map((account) => account.id)),
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const purpleButtonSx = {
    bgcolor: MONETA_PURPLE,
    color: "#fff",
    borderRadius: 1,
    fontFamily: FONT_STACK,
    fontWeight: 700,
    textTransform: "none",
    px: 3,
    boxShadow: "none",
    "&:hover": { bgcolor: "#682579", boxShadow: "none" },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: MONETA_PAGE_BG,
        fontFamily: FONT_STACK,
      }}
    >
      <MonetaHeader onCancel={onCancel} />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {step === "login" && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "5fr 6fr" },
              gap: 4,
              alignItems: "start",
            }}
          >
            <MonetaConsentPanel thirdPartyName={thirdPartyName} />

            <Box
              sx={{
                bgcolor: "#fff",
                border: `1px solid ${MONETA_BORDER}`,
                borderRadius: 2,
                p: 3.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 20,
                  color: MONETA_NAVY,
                  fontFamily: FONT_STACK,
                  mb: 0.5,
                }}
              >
                Přihlášení QR kódem
              </Typography>
              <Typography
                sx={{
                  fontSize: 13.5,
                  color: MONETA_NAVY,
                  fontFamily: FONT_STACK,
                  mb: 2,
                }}
              >
                Naskenujte QR kód a potvrďte přihlášení ve Smart Bance
              </Typography>

              <Box
                sx={{
                  width: 150,
                  height: 150,
                  border: `1px solid ${MONETA_BORDER}`,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#fafafa",
                  mb: 2.5,
                }}
              >
                <Typography sx={{ fontSize: 56 }}>▦</Typography>
              </Box>

              <Divider sx={{ my: 2 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: MONETA_NAVY,
                    fontFamily: FONT_STACK,
                  }}
                >
                  nebo
                </Typography>
              </Divider>

              <Box sx={{ mb: 2 }}>
                <MonetaFieldLabel>
                  ID (přihlašovací jméno do IB)
                </MonetaFieldLabel>
                <TextField
                  value={loginName}
                  onChange={(event) => setLoginName(event.target.value)}
                  sx={monetaInputSx}
                />
                <Link
                  sx={{
                    display: "inline-block",
                    color: MONETA_PURPLE,
                    fontSize: 13,
                    fontFamily: FONT_STACK,
                    cursor: "pointer",
                    mt: 0.75,
                  }}
                >
                  ? Nepamatuji si své klientské číslo / ID
                </Link>
              </Box>

              <Box sx={{ mb: 2 }}>
                <MonetaFieldLabel>Den a měsíc narození</MonetaFieldLabel>
                <Stack direction="row" sx={{ gap: 1.5 }}>
                  <Select
                    displayEmpty
                    value={birthDay}
                    onChange={(event) => setBirthDay(event.target.value)}
                    sx={{
                      bgcolor: "#fff",
                      flex: 1,
                      fontFamily: FONT_STACK,
                      fontSize: 14,
                      borderRadius: 1,
                    }}
                  >
                    <MenuItem value="">Den</MenuItem>
                    {DAYS.map((day) => (
                      <MenuItem key={day} value={day}>
                        {day}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    displayEmpty
                    value={birthMonth}
                    onChange={(event) => setBirthMonth(event.target.value)}
                    sx={{
                      bgcolor: "#fff",
                      flex: 1,
                      fontFamily: FONT_STACK,
                      fontSize: 14,
                      borderRadius: 1,
                    }}
                  >
                    <MenuItem value="">Měsíc</MenuItem>
                    {MONTHS.map((month) => (
                      <MenuItem key={month} value={month}>
                        {month}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              </Box>

              <Stack
                direction="row"
                onClick={() => setRememberMe((prev) => !prev)}
                sx={{ alignItems: "center", gap: 0.5, cursor: "pointer", mb: 2 }}
              >
                <Checkbox
                  size="small"
                  checked={rememberMe}
                  sx={{
                    p: 0,
                    color: MONETA_PURPLE,
                    "&.Mui-checked": { color: MONETA_PURPLE },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 14,
                    color: MONETA_NAVY,
                    fontFamily: FONT_STACK,
                  }}
                >
                  Zapamatuj si mě
                </Typography>
              </Stack>

              <Button
                variant="contained"
                onClick={() => setStep("accounts")}
                disabled={!loginName}
                sx={{ ...purpleButtonSx, width: "100%" }}
              >
                › Pokračovat
              </Button>
            </Box>
          </Box>
        )}

        {step === "accounts" && (
          <Box sx={{ maxWidth: 640 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 24,
                color: MONETA_NAVY,
                fontFamily: FONT_STACK,
                mb: 2.5,
              }}
            >
              Nabídka účtů
            </Typography>

            <Box
              data-tour="bank-accounts"
              sx={{
                bgcolor: "#fff",
                border: `1px solid ${MONETA_BORDER}`,
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack sx={{ gap: 0.5 }}>
                {accounts.map((account) => (
                  <Stack
                    key={account.id}
                    direction="row"
                    onClick={() => toggle(account.id)}
                    sx={{
                      alignItems: "center",
                      gap: 1,
                      cursor: "pointer",
                      py: 0.75,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(account.id)}
                      sx={{
                        p: 0,
                        color: MONETA_PURPLE,
                        "&.Mui-checked": { color: MONETA_PURPLE },
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: MONETA_NAVY,
                        fontFamily: FONT_STACK,
                      }}
                    >
                      {account.name}&nbsp;&nbsp;{account.accountNumber}&nbsp;&nbsp;(
                      {account.currency})
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <Stack direction="row" sx={{ gap: 1.5, mt: 3 }}>
              <Button
                data-tour="bank-connect-submit"
                variant="contained"
                disabled={selectedIds.size === 0}
                onClick={() =>
                  onConnect(
                    accounts.filter((account) => selectedIds.has(account.id)),
                  )
                }
                sx={purpleButtonSx}
              >
                Potvrdit
              </Button>
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{
                  color: MONETA_NAVY,
                  borderColor: MONETA_BORDER,
                  borderRadius: 5,
                  textTransform: "none",
                  fontFamily: FONT_STACK,
                  px: 2.5,
                  "&:hover": {
                    bgcolor: "#f5f5f5",
                    borderColor: MONETA_BORDER,
                  },
                }}
              >
                Zamítnout
              </Button>
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  );
}
